import { Network, Trash2, HelpCircle, Download, X, BookOpen, Sparkles } from 'lucide-react';
import Canvas from './components/Canvas';
import DevicePalette from './components/DevicePalette';
import Terminal from './components/Terminal';
import DeviceConfig from './components/DeviceConfig';
import LessonPanel from './components/LessonPanel';
import LessonBuilder from './components/LessonBuilder';
import HelpModal from './components/HelpModal';
import useStore from './lib/store';
import './App.css';

export default function App() {
  const panelOpen = useStore(state => state.panelOpen);
  const panelType = useStore(state => state.panelType);
  const setPanelOpen = useStore(state => state.setPanelOpen);
  const setPanelType = useStore(state => state.setPanelType);
  const clearAll = useStore(state => state.clearAll);
  const selectedDevice = useStore(state => state.selectedDevice);
  const devices = useStore(state => state.devices);
  const connections = useStore(state => state.connections);
  const lessonMode = useStore(state => state.lessonMode);

  const handleClear = () => {
    if (lessonMode) {
      alert('Exit lesson mode first before clearing the network.');
      return;
    }
    if (confirm('Are you sure you want to clear all devices and connections? This cannot be undone.')) {
      clearAll();
    }
  };

  const handleExport = () => {
    const data = {
      devices,
      connections,
      timestamp: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenLessons = () => {
    setPanelType('lesson');
    setPanelOpen(true);
  };

  const handleOpenBuilder = () => {
    setPanelType('builder');
    setPanelOpen(true);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <div className="logo">
            <Network size={32} strokeWidth={2.5} />
            <h1>NetMappr Edu</h1>
          </div>
          <span className="subtitle">Interactive Network Simulator</span>
        </div>

        <div className="header-actions">
          <button 
            className="btn btn-primary" 
            onClick={handleOpenLessons}
            title="Browse Lessons"
          >
            <BookOpen size={18} />
            Lessons
          </button>
          
          <button 
            className="btn btn-pro" 
            onClick={handleOpenBuilder}
            title="Create Custom Lesson"
          >
            <Sparkles size={18} />
            Create Lesson
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handleExport} 
            title="Export Configuration"
          >
            <Download size={18} />
            Export
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handleClear} 
            title="Clear All"
            disabled={lessonMode}
          >
            <Trash2 size={18} />
            Clear All
          </button>
          
          <button className="btn btn-secondary" onClick={() => {}}>
            <HelpCircle size={18} />
            Help
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="main-layout">
        {/* Left Sidebar - Device Palette */}
        {!lessonMode && panelType !== 'builder' && (
          <aside className="sidebar sidebar-left">
            <DevicePalette />
          </aside>
        )}

        {/* Canvas */}
        <main className={`canvas-container ${panelOpen ? 'panel-open' : ''} ${lessonMode || panelType === 'builder' ? 'lesson-mode' : ''}`}>
          <Canvas />
        </main>

        {/* Sliding Panel */}
        <aside className={`sliding-panel ${panelOpen ? 'open' : ''} ${panelType === 'builder' ? 'builder-panel' : ''}`}>
          <div className="panel-header">
            <h3>
              {panelType === 'terminal' && 'Terminal'}
              {panelType === 'config' && 'Configuration'}
              {panelType === 'lesson' && 'Lesson'}
              {panelType === 'builder' && 'Lesson Builder'}
            </h3>
            <button 
              className="panel-close"
              onClick={() => setPanelOpen(false)}
              title="Close panel"
            >
              <X size={20} />
            </button>
          </div>

          <div className="panel-content">
            {panelType === 'terminal' && <Terminal />}
            {panelType === 'config' && <DeviceConfig />}
            {panelType === 'lesson' && <LessonPanel />}
            {panelType === 'builder' && <LessonBuilder />}
          </div>
        </aside>
      </div>
    </div>
  );
}
